import React, { useState } from "react";
import { Link } from 'react-router-dom';
import validateEmail from "../../utils/validateEmail";
import StepsLineWithCircles from "../../components/Steps/StepsLineWithCircles";
import useApp from '../../store/app/appContext';
import EmailInput from "../../components/Inputs/EmailInput";
import TextInput from "../../components/Inputs/TextInput";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import PasswordInput from "../../components/Inputs/PasswordInput";
import ReactSelect from "../../components/Inputs/ReactSelect";
import { COUNTRIES } from '../../helpers/selectOptions';
import DateInput from "../../components/Inputs/DateInput";


function RegisterForm() {
  
  const {
    register
  } = useApp();

  const [pwCheck, _setPwCheck] = useState({
      first: '',
      second: '',
  });

  const [formError, setFormError] = useState({
    field: '',
    msg: ''
  });

  const setPwCheck = (newVal) => _setPwCheck({ ...pwCheck, ...newVal });
  
  const [user, _setUser] = useState({
      firstName: null,
      email: null,
      lastName: null,
      password: null,
      address: null,
      city: null,
      country: null,
      postalCode: null,
      mobileNumber: null,
      sex: null,
      dateOfBirth: null,
      userType: 'patient'
  });

  const setUser = (newVal) => _setUser({ ...user, ...newVal });
  
  const [registerSteps, setRegisterSteps] = useState([
    { name: 'Account information', step: 1, status: 'current' },
    { name: 'Personal information', step: 2, status: 'upcoming' },
    { name: 'Contact information', step: 3, status: 'upcoming' },
  ])

  const currentStep = registerSteps.find(element => element.status === 'current');

  const handleRegisterClick = () => {
    if (currentStep.step === 1) {
      if (!validateEmail(user.email)) {
        return setFormError({
          field: 'email',
          msg: "Please enter a valid email."
        });
      }
      if (!/^[a-z0-9]+$/i.test(pwCheck.first) || pwCheck.first.length < 8) {
        return setFormError({
          field: 'password',
          msg: 'Your password must be alphanumeric and contain at least 8 characters.'
        });
      }
      if (pwCheck.first !== pwCheck.second) {
        return setFormError({
          field: 'password',
          msg: "Your passwords don't match."
        });
      }
      setFormError({ field: '', msg:'' });
      setUser({ password: pwCheck.first });
      return setRegisterSteps([
        { name: 'Account information', step: 1, status: 'complete' },
        { name: 'Personal information', step: 2, status: 'current' },
        { name: 'Contact information', step: 3, status: 'upcoming' },
      ]);
    }
    if (currentStep.step === 2 ) {
      if (user.firstName === '' || user.firstName === null ) {
        return setFormError({
          field: 'firstName',
          msg: "Please enter your first name(s)."
        });
      }
      if (user.lastName === '' || user.lastName === null) {
        return setFormError({
          field: 'lastName',
          msg: "Please enter your last name(s)."
        });
      }
      if (user.dateOfBirth === '' || user.dateOfBirth=== null ) {
        return setFormError({
          field: 'dateOfBirth',
          msg: "Please enter your date of birth."
        });
      }
      if (user.sex === '' || user.sex === null ) {
        return setFormError({
          field: 'sex',
          msg: "Please enter your sex."
        });
      }
      if (user.mobileNumber === '' || user.mobileNumber=== null ) {
        return setFormError({
          field: 'mobileNumber',
          msg: "Please enter your mobile number."
        });
      }
      return setRegisterSteps([
        { name: 'Account information', step: 1, status: 'complete' },
        { name: 'Personal information', step: 2, status: 'complete' },
        { name: 'Contact information', step: 3, status: 'current' },
      ]);
    }
    if (currentStep.step === 3) {
      if (user.country === '' || user.country === null ) {
        return setFormError({
          field: 'country',
          msg: "Please enter your country."
        });
      } else if (user.city === '' || user.city === null ) {
        return setFormError({
          field: 'city',
          msg: "Please enter your city"
        });
      }
      else if (user.address === '' || user.address === null ) {
        return setFormError({
          field: 'address',
          msg: "Please enter your address."
        });
      }
      else if (user.postalCode === '' || user.postalCode === null ) {
        return setFormError({
          field: 'postalCode',
          msg: "Please enter your postal code."
        });
      }
      
      else return register(user);
    }
  };

  const handleBackClick = () => {
    if (currentStep.step === 2) {
      return setRegisterSteps([
        { name: 'Account information', step: 1, status: 'current' },
        { name: 'Personal information', step: 2, status: 'upcoming' },
        { name: 'Contact information', step: 3, status: 'upcoming' },
      ]);
    }
    if (currentStep.step === 3) {
      return setRegisterSteps([
        { name: 'Account information', step: 1, status: 'complete' },
        { name: 'Personal information', step: 2, status: 'current' },
        { name: 'Contact information', step: 3, status: 'upcoming' },
      ]);
    }
  };
  
  const countryOptions = [];
  if (!countryOptions.length) COUNTRIES.forEach(country => {
    countryOptions.push({
      value: country,
      label: country
    })
  });

  const sexOptions = [
    {
      value: 'Male',
      label: 'Male'
    },
    {
      value: 'Female',
      label: 'Female'
    },
    {
      value: 'Other',
      label: 'Other'
    },
  ];
  
  return (
     <div className="w-full md:max-w-400">
      <div className='w-full mb-4'>
        <p tabIndex={0} role="heading" aria-level="1" aria-label="Register account" className="text-2xl md:text-4xl text-secondary">
            Register an account
        </p>
        <p className="text-xs md:text-sm xl:mt-4 mt-2 leading-none w-full text-secondary">
          Already have an account?{" "}
          <span tabIndex={0} role="link" aria-label="Login here" className="text-sm font-medium leading-none underline text-gray-800 cursor-pointer">
              {" "}
              <Link to='/login'> Login here </Link>
          </span>
        </p>
      </div>
      <div className='w-full mt-6 mb-12'>
        <StepsLineWithCircles steps={registerSteps}/>
      </div>
      <div className="grid grid-cols-2 gap-y-2 gap-x-2">
        {currentStep.step === 1 &&
        //Account Info
        <>
          <div className='col-span-2'>
            <EmailInput value={user.email} placeholder={'Enter your email'} onChange={(e) => setUser({ email: e.target.value })}/>
          </div>
          {formError.field === 'email' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='relative col-span-2'>
            <PasswordInput label="Enter password" value={pwCheck.first} placeholder={'Enter your password'} onChange={(e) => setPwCheck({ first: e.target.value })}/>
          </div>
          <div className='col-span-2'>
            <PasswordInput label="Confirm password" value={pwCheck.second} placeholder={'Confirm password'} onChange={(e) => setPwCheck({ second: e.target.value })}/>
          </div>
          {formError.field === 'password' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
        </>
        }
        {currentStep.step === 2 &&
        <>
          <div className='col-span-2'>
            <TextInput value={user.firstName} label={'First name*'} placeholder={'Enter your first name'} onChange={(e) => setUser({ firstName: e.target.value })}/>
          </div>
          {formError.field === 'firstName' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <TextInput value={user.lastName} label={'Last name*'} placeholder={'Enter your last name'} onChange={(e) => setUser({ lastName: e.target.value })}/>
          </div>
          {formError.field === 'lastName' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <DateInput id='DoB' label='Date of birth*' inputClass='col-span-3' data={user.dateOfBirth} onChange={(e) => setUser({dateOfBirth: e.target.value})} />
          </div>
          {formError.field === 'dateOfBirth' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <ReactSelect options={sexOptions} label={'Sex*'} value={ user.sex } onChange={(e) => setUser({ sex: e.value })}/>
          </div>
          {formError.field === 'sex' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <TextInput value={user.mobileNumber} label={'Phone*'} placeholder={'Enter your phone number'} onChange={(e) => setUser({ mobileNumber: e.target.value })}/>
          </div>
          {formError.field === 'mobileNumber' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
        </>
        }
        {currentStep.step === 3 &&
        <>
          <div className='col-span-2'>
            <ReactSelect options={countryOptions} value={ user.country } label={'Country*'} onChange={(e) => setUser({ country: e.value })}/>
          </div>
          {formError.field === 'country' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <TextInput value={user.city} label={'City*'} placeholder={'Enter your city of residence'} onChange={(e) => setUser({ city: e.target.value })}/>
          </div>
          {formError.field === 'city' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <TextInput value={user.address} label={'Address*'} placeholder={'Enter your address'} onChange={(e) => setUser({ address: e.target.value })}/>
          </div>
          {formError.field === 'address' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
          <div className='col-span-2'>
            <TextInput value={user.postalCode} label={'Postal code*'} placeholder={'Enter your postal code'} onChange={(e) => setUser({ postalCode: e.target.value })}/>
          </div>
          {formError.field === 'postalCode' && 
          <span className="text-red-400 text-sm col-span-2">{formError.msg}</span>
          }
        </>
        }
        {currentStep.step !== 1 &&
          <div className='col-span-1'>
            <SecondaryButton className="mt-8" large onClick={handleBackClick} text={'BACK'}/>
          </div>
        }
        <div className={currentStep.step === 1 ? 'col-span-2' : 'col-span-1'}>
          <PrimaryButton className="mt-8" large onClick={handleRegisterClick} text={currentStep.step === 3 ? 'REGISTER' : 'NEXT'}/>
        </div>
      </div>
    </div>
  )
};

export default RegisterForm;

